'use client';

import { useEffect, useMemo, useState } from 'react';
import { LocalStorageHistoryStore } from '@/history/localStorage.store';
  
type Distribution = {
  1: number;
  2: number;
  3: number;
  4: number;
  5: number;
  6: number;
  lost: number;
};

type TimeStats = {
  count: number;
  /** Le plus de temps restant (meilleure perf). */
  bestMs: number;
  /** Le moins de temps restant (pire perf). */
  worstMs: number;
  avgMs: number;
};

type Stats = {
  totalGames: number;
  winRate: number;
  currentStreak: number;
  bestStreak: number;
  distribution: Distribution;
  /** Stats de temps hardcore (victoires chronométrées). `null` si aucune. */
  timeStats: TimeStats | null;
};

export function useWordleStats(storageNamespace?: string, refreshKey?: number) {
  const [stats, setStats] = useState<Stats | null>(null);
  const historyStore = useMemo(
    () => new LocalStorageHistoryStore(storageNamespace),
    [storageNamespace]
  );

  useEffect(() => {
    async function compute() {
      const history = await historyStore.loadAll();

      const finishedGames = history.filter(
        h => h.status === 'won' || h.status === 'lost'
      );

      finishedGames.sort((a, b) =>
        a.dateKey.localeCompare(b.dateKey)
      );

      const totalGames = finishedGames.length;
      const wins = finishedGames.filter(h => h.status === 'won');
  
      const winRate =
        totalGames === 0
          ? 0
          : Math.round((wins.length / totalGames) * 100);

      const distribution: Distribution = {
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
        6: 0,
        lost: 0,
      };

      finishedGames.forEach(h => {
        if (h.status === 'won') {
          const attempts = h.guesses.length;
  
          if (attempts >= 1 && attempts <= 6) {
            distribution[attempts as 1 | 2 | 3 | 4 | 5 | 6]++;
          }
        } else {
          distribution.lost++;
        }
      });
  
      let bestStreak = 0;
      let running = 0;

      finishedGames.forEach(h => {
        if (h.status === 'won') {
          running++;
          bestStreak = Math.max(bestStreak, running);
        } else {
          running = 0;
        }
      });

      let currentStreak = 0;
      for (let i = finishedGames.length - 1; i >= 0; i--) {
        if (finishedGames[i]?.status === 'won') currentStreak++;
        else break;
      }

      // Temps (hardcore) : sur les victoires chronométrées uniquement.
      const winTimes = wins
        .map(h => h.hardcoreTimeRemainingMs)
        .filter((v): v is number => typeof v === 'number');

      const timeStats: TimeStats | null =
        winTimes.length > 0
          ? {
              count: winTimes.length,
              bestMs: Math.max(...winTimes),
              worstMs: Math.min(...winTimes),
              avgMs:
                winTimes.reduce((sum, v) => sum + v, 0) / winTimes.length,
            }
          : null;

      setStats({
        totalGames,
        winRate,
        currentStreak,
        bestStreak,
        distribution,
        timeStats,
      });
    }

    compute();
  }, [historyStore, refreshKey]);

  return stats;
}
